import { Component, HostListener, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';
import { StorageService } from '@app/core/services/client-services/storage-service/storage.service';
import { ToastService } from '@app/core/services/toast.service';
import { LoginService } from '@app/shared/services/login.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { DiscrepanciesService } from 'api/services';
import { AuthService } from '@app/core/services/auth.service';
import { lastValueFrom } from 'rxjs';
import { PublicService } from '../../../../api-v3/services/public.service';
import { Announcement } from '../../../../api-v3/models/announcement';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { NgClass } from '@angular/common';
import { UnauthFooterComponent } from '../../components/unauth-footer/unauth-footer.component';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrl: './login.component.scss',
    imports: [
        MatIcon,
        MatButton,
        NgClass,
        UnauthFooterComponent,
        TranslateModule,
    ],
})
export class LoginComponent implements OnInit {
    @HostListener('document:keydown.enter', ['$event'])
    loginForm = new FormGroup({
        username: new FormControl('', Validators.required),
        password: new FormControl('', Validators.required),
    });
    isLoading = signal(false);
    hideInfo = signal(false);

    announcements = signal<Announcement[]>([]);

    constructor(
        private toastr: ToastService,
        private translate: TranslateService,
        private storageService: StorageService,
        private activeRoute: ActivatedRoute,
        private authService: AuthService,
        private sessionService: SessionService,
        private discrepanciesService: DiscrepanciesService,
        private loginService: LoginService,
        private publicService: PublicService
    ) {}

    async ngOnInit(): Promise<void> {
        this.activeRoute.queryParams
            .pipe(untilDestroyed(this))
            .subscribe(params => {});

        this.hideInfo.set(
            this.storageService.getItem('hideInfo') == 'true' ? true : false);

        // Check if user is already authenticated (has valid session cookie)
        try {
            this.isLoading.set(true);
            await this.sessionService.loadUserFromApi();

            // User is authenticated -- route to appropriate page
            try {
                await this.loadDiscrepancyChoices();
            } catch (e) {}

            this.isLoading.set(false);
            this.loginService.routeToPage(
                this.sessionService.getSession()?.termsOfService?.accepted
            );
        } catch (error) {
            // Not authenticated -- show login button
            this.isLoading.set(false);
        }

        await this.loadAndFilterAnnouncements();
    }

    login(): void {
        this.isLoading.set(true);
        try {
            this.authService.initLogin();
        } catch (error) {
            console.error('Failed to initiate login:', error);
            this.isLoading.set(false);
        }
    }

    private async loadDiscrepancyChoices(): Promise<void> {
        try {
            const response = await lastValueFrom(
                this.discrepanciesService.getDiscrepanciesChoices()
            );
            this.storageService.setItem('discrepancyChoices', response);
        } catch (error) {
            console.error('Failed to load discrepancy choices:', error);
        }
    }

    private async loadAndFilterAnnouncements(): Promise<void> {
        try {
            const announcements: Announcement[] = await lastValueFrom(
                this.publicService.getGlobalAnnouncements()
            );

            const currentTimestamp = Date.now();

            this.announcements.set(announcements
                .filter(announcement => {
                    const hasStarted =
                        announcement.startingAt <= currentTimestamp;
                    const hasNotEnded =
                        announcement.endingAt === null ||
                        announcement.endingAt === undefined ||
                        announcement.endingAt > currentTimestamp;

                    return hasStarted && hasNotEnded;
                })
                .sort((a, b) => b.startingAt - a.startingAt));
        } catch (error) {
            this.toastr.error(
                this.translate.instant('ADMIN-PANEL.ANNOUNCEMENTS.GET-ERROR')
            );
        }
    }
}
